// src/Views/Dashboard/Dashboard.js
// UPDATED: February 1, 2026
// - 🔥 REMOVED: Free trial option for web (existing trials still honored)
// - Simplified upgrade flow - direct to subscription
// - V10 Limits - Premium: 5/day, 30/week
// - 🔄 STRATEGIC UPDATE: Football AI now routes to AiAssistant

import React, { useEffect } from 'react';
import './Dashboard.css';
import { Icon } from '@iconify/react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../../context/AuthContext';

// 🎯 Import Profit Guide Popup
import ProfitGuide from '../ProfitGuide/ProfitGuide';

const Dashboard = () => {
    const navigate = useNavigate();
    
    const { 
        user, 
        profile,
        loading: authLoading, 
        profileLoading,
        isPremium,
        isPaidPremium,
        isTrialActive,      // Keep for existing trial users
        trialDaysLeft,      // Keep for existing trial users
        hasUsedTrial        // Keep to detect expired trials
    } = useAuth();

    // Redirect if not logged in
    useEffect(() => {
        if (!authLoading && !user) {
            navigate('/signin');
        }
    }, [user, authLoading, navigate]);

    // =========================================================
    // STATUS LOGIC
    // =========================================================
    
    const isTrialExpired = hasUsedTrial && !isTrialActive;
    const hasAccess = isPremium || isTrialActive;

    // 🔥 UPDATED: No trial option - direct to pricing for subscription
    const handleLockedClick = (path, featureName) => {
        if (hasAccess) {
            navigate(path);
        } else {
            if (window.confirm(`🔒 ${featureName} is locked.\n\nSubscribe to Premium to unlock?`)) {
                navigate('/pricing');
            }
        }
    };

    // =========================================================
    // RENDER
    // =========================================================
    
    // Loading State
    if (authLoading) {
        return (
            <section className="dash">
                <div className="dash__inner">
                    <div className="dash__card dash__card--status" style={{ marginTop: '2rem', minHeight: '200px', display:'flex', alignItems:'center', justifyContent:'center' }}>
                        <div style={{textAlign:'center', color:'#94a3b8'}}>
                            <Icon icon="solar:restart-bold" style={{fontSize:'2rem', animation:'spin 1s linear infinite', marginBottom:'1rem'}} />
                            <p>Loading Profile...</p>
                        </div>
                    </div>
                </div>
                <style>{`
                    @keyframes spin {
                        from { transform: rotate(0deg); }
                        to { transform: rotate(360deg); }
                    }
                `}</style>
            </section>
        );
    }

    if (!user) return null;

    // Plan Label & Tagline - 🔥 UPDATED: No trial messaging for new users
    const planLabel = isPaidPremium ? 'Premium Plan' : isTrialActive ? 'Trial Access' : 'Freemium';
    
    const planTagline = (() => {
        if (isPaidPremium) return 'V10 Engine: Active (5/day, 30/week).';
        
        // Keep trial messaging for EXISTING trial users only
        if (isTrialActive && trialDaysLeft > 0) {
            return `Trial Active: ${trialDaysLeft} day${trialDaysLeft !== 1 ? 's' : ''} left (1 search/day).`;
        }
        
        if (isTrialExpired) return 'Access expired. Subscribe to continue.';
        
        // 🔥 NEW: Direct subscription messaging (no trial mention)
        return 'Subscribe to unlock V10 Engine & all features.';
    })();

    return (
        <section className="dash">
            {/* 🎯 Profit Guide Popup - Shows once after login until dismissed */}
            <ProfitGuide isPopup={true} />

            <div className="dash__inner">

                <header className="dash__header">
                    <div className="dash__eyebrow">
                        <Icon icon="solar:user-id-linear" className="dash__eyebrow-icon" />
                        <span>Command Center</span>
                    </div>
                    <h1 className="dash__title">
                        Welcome, <span className="dash__name">{user.displayName || profile?.displayName || 'Trader'}</span>.
                    </h1>
                    <p className="dash__subtitle">
                        System status: <span className="dash__status-live">V10 Online</span>. Markets active.
                    </p>
                </header>

                {/* Trial Banner - ONLY for EXISTING trial users */}
                {isTrialActive && trialDaysLeft > 0 && (
                    <div className={`trial-banner ${trialDaysLeft <= 1 ? 'trial-banner--urgent' : ''}`}>
                        <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
                            <Icon icon={trialDaysLeft <= 1 ? "solar:alarm-bold" : "solar:clock-circle-bold"} style={{ fontSize: '1.4rem' }} />
                            <span className="trial-text">
                                Access Pass: <strong>{trialDaysLeft} Day{trialDaysLeft !== 1 ? 's' : ''} Left</strong>
                            </span>
                        </div>
                        <button className="trial-upgrade-btn" onClick={() => navigate('/pricing')}>Upgrade (5/day)</button>
                    </div>
                )}

                {/* Trial Expired Banner - ONLY for users who HAD a trial */}
                {isTrialExpired && !isPaidPremium && (
                    <div className="trial-banner trial-banner--expired" style={{ 
                        background: 'rgba(239, 68, 68, 0.1)', 
                        borderColor: 'rgba(239, 68, 68, 0.3)' 
                    }}>
                        <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
                            <Icon icon="solar:danger-triangle-bold" style={{ fontSize: '1.4rem', color: '#ef4444' }} />
                            <span className="trial-text" style={{ color: '#f87171' }}>
                                Trial Expired - <strong>Subscribe to continue</strong>
                            </span>
                        </div>
                        <button 
                            className="trial-upgrade-btn" 
                            onClick={() => navigate('/pricing')}
                            style={{ background: '#ef4444', borderColor: '#ef4444' }}
                        >
                            Subscribe Now
                        </button>
                    </div>
                )}

                <div className="dash__grid">
                    {/* Status Card */}
                    <div className="dash__status-col">
                        <div className="dash__card dash__card--status">
                            <div className="dash__status-header">
                                <div>
                                    <p className="dash__plan-label">{planLabel}</p>
                                    <p className="dash__plan-tagline">{planTagline}</p>
                                </div>
                                <div className="dash__plan-icon">
                                    <Icon icon={isPaidPremium ? 'solar:crown-bold' : isTrialActive ? 'solar:hourglass-bold' : 'solar:user-bold'} />
                                </div>
                            </div>
                            
                            {/* 🔥 UPDATED: No active plan -> Simple Subscribe Button (No Trial) */}
                            {!isPaidPremium && !isTrialActive && (
                                <div style={{ display: 'flex', flexDirection: 'column', gap: '8px', marginTop: '1rem' }}>
                                    <button 
                                        type="button" 
                                        className="dash__upgrade-btn" 
                                        style={{ 
                                            background: 'linear-gradient(135deg, #10b981, #059669)', 
                                            borderColor: '#10b981', 
                                            color: '#fff' 
                                        }} 
                                        onClick={() => navigate('/pricing')}
                                    >
                                        <Icon icon="solar:bolt-bold" />
                                        Subscribe Now
                                    </button>

                                    <p style={{ 
                                        color: '#64748b', 
                                        fontSize: '0.7rem', 
                                        textAlign: 'center',
                                        margin: '4px 0 0 0'
                                    }}>
                                        5 searches/day • Full V10 Access • Cancel anytime
                                    </p>
                                </div>
                            )}
                            
                            {/* EXISTING Trial Active -> Upgrade to Full Premium */}
                            {isTrialActive && !isPaidPremium && (
                                <button 
                                    type="button" 
                                    className="dash__upgrade-btn" 
                                    onClick={() => navigate('/pricing')}
                                >
                                    <Icon icon="solar:bolt-bold" />
                                    Get Full Premium (5/day)
                                </button>
                            )}
                            
                            {/* Paid Premium - Show Active Badge */}
                            {isPaidPremium && (
                                <div style={{
                                    display: 'flex',
                                    alignItems: 'center',
                                    gap: '8px',
                                    color: '#4ade80',
                                    fontSize: '0.9rem',
                                    marginTop: '0.5rem'
                                }}>
                                    <Icon icon="solar:verified-check-bold" />
                                    <span>Premium Active</span>
                                </div>
                            )}
                        </div>
                    </div>

                    {/* Actions Grid */}
                    <div className="dash__actions-col">
                        <div className="dash__actions-grid">
                            
                            {/* 🔄 UPDATED: Football AI now routes to AiAssistant */}
                            <button className="dash__action-card" onClick={() => navigate('/ai-assistant')}>
                                <div className="dash__action-icon dash__action-icon--blue"><Icon icon="solar:football-bold" /></div>
                                <div className="dash__action-content">
                                    <h3 className="dash__action-title">Football AI</h3>
                                    <p className="dash__action-desc">AiAssistant Engine</p>
                                </div>
                                <Icon icon="solar:arrow-right-linear" className="dash__arrow" />
                            </button>

                            <button 
                                className="dash__action-card" 
                                onClick={() => navigate('/crypto')}
                            >
                                <div className="dash__action-icon dash__action-icon--gold"><Icon icon="solar:chart-2-linear" /></div>
                                <div className="dash__action-content">
                                    <h3 className="dash__action-title">Crypto AI</h3>
                                    <p className="dash__action-desc">Auto-trading bot</p>
                                </div>
                                <Icon icon="solar:arrow-right-linear" className="dash__arrow" />
                            </button>

                            <button className="dash__action-card" onClick={() => navigate('/ai-assistant')}>
                                <div className="dash__action-icon dash__action-icon--purple"><Icon icon="solar:magic-stick-3-linear" /></div>
                                <div className="dash__action-content">
                                    <h3 className="dash__action-title">AiAssistant</h3>
                                    <p className="dash__action-desc">V10.0 Engine</p>
                                </div>
                                <Icon icon="solar:arrow-right-linear" className="dash__arrow" />
                            </button>

                            <button className="dash__action-card" onClick={() => navigate('/profile')}>
                                <div className="dash__action-icon dash__action-icon--gray"><Icon icon="solar:settings-linear" /></div>
                                <div className="dash__action-content">
                                    <h3 className="dash__action-title">Settings</h3>
                                    <p className="dash__action-desc">Manage account</p>
                                </div>
                                <Icon icon="solar:arrow-right-linear" className="dash__arrow" />
                            </button>
                        </div>
                    </div>

                    {/* 🎯 REPLACED: Profit Guide Quick Link (was Risk Management) */}
                    <div className="dash__notice-col">
                        <div className="dash__card dash__card--notice dash__card--guide">
                            <div className="dash__notice-icon">
                                <Icon icon="solar:cup-star-bold" />
                            </div>
                            <p className="dash__notice-text">Learn how to <strong>make consistent profits</strong> with AiPredict.</p>
                            <button className="dash__link-btn dash__link-btn--guide" onClick={() => navigate('/guide')}>
                                <Icon icon="solar:book-2-bold" />
                                Read Profit Guide
                            </button>
                        </div>
                    </div>
                </div>
            </div>
        </section>
    );
};

export default Dashboard;